import React, { useRef, useEffect, useCallback } from 'react';

// --- TYPESCRIPT INTERFACES ---
interface Point {
    x: number;
    y: number;
}

interface SharinganCanvasProps {
    animationTrigger: { seed: number; userDescription: string; primaryPattern: number } | null;
    onAnimationComplete: (userDescription: string, primaryPattern: number) => void;
}

// --- SHARINGAN CANVAS COMPONENT ---
export const SharinganCanvas: React.FC<SharinganCanvasProps> = ({ animationTrigger, onAnimationComplete }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isAnimating = useRef(false);
    const lastDrawnState = useRef<any>(null);

    const seededRandom = useCallback((randFunc: () => number, min: number, max: number) => randFunc() * (max - min) + min, []);
    const seededRandomInt = useCallback((randFunc: () => number, min: number, max: number) => Math.floor(seededRandom(randFunc, min, max + 1)), [seededRandom]);
    const createSeededRandom = (seed: number) => { let s = seed; return () => { s = (1664525 * s + 1013904223) % 2**32; return s / 2**32; }; };

    const drawBaseIris = useCallback((ctx: CanvasRenderingContext2D, radius: number, center: Point, inverted = false) => {
        if (inverted) {
            ctx.fillStyle = 'black';
            ctx.beginPath();
            ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
            ctx.fill();
        } else {
            const gradient = ctx.createRadialGradient(center.x, center.y, radius * 0.6, center.x, center.y, radius);
            gradient.addColorStop(0, '#ff1a1a'); gradient.addColorStop(0.8, '#c50000'); gradient.addColorStop(1, '#6e0000');
            ctx.fillStyle = gradient; ctx.beginPath(); ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI); ctx.fill();
        }
    }, []);

    const drawPupil = useCallback((ctx: CanvasRenderingContext2D, pupilRadius: number, center: Point) => {
        ctx.fillStyle = 'black'; ctx.beginPath(); ctx.arc(center.x, center.y, pupilRadius, 0, 2 * Math.PI); ctx.fill();
    }, []);

    const drawTomoe = useCallback((ctx: CanvasRenderingContext2D, radius: number, rotation = 0) => {
        const TOMOE_SCALE = 1.7;
        const TOMOE_ORBIT_RADIUS_FACTOR = 0.6;
        const TOMOE_INDIVIDUAL_ROTATIONS_DEGREES = [0, 120, 240];
        const centerX = ctx.canvas.width / 2;
        const centerY = ctx.canvas.height / 2;
        const positionRadius = radius * TOMOE_ORBIT_RADIUS_FACTOR;

        for (let i = 0; i < 3; i++) {
            ctx.save();
            ctx.translate(centerX, centerY);
            const angle = (i * 2 * Math.PI / 3) + rotation;
            const individualRotationRadians = TOMOE_INDIVIDUAL_ROTATIONS_DEGREES[i] * Math.PI / 180;
            const shapeAngle = angle + individualRotationRadians;
            ctx.fillStyle = 'black';
            const cx = positionRadius * Math.sin(angle);
            const cy = positionRadius * Math.cos(angle);
            ctx.translate(cx, cy);
            ctx.scale(TOMOE_SCALE, TOMOE_SCALE);
            const headRadius = (radius / 24); 
            ctx.beginPath();
            ctx.arc(0, 0, headRadius, 0, Math.PI * 2, true);
            const tailCurveRadius = (radius / 4.2) / TOMOE_SCALE;
            const tailEndRadius = (radius / 4) / TOMOE_SCALE;
            const startPointAngle = shapeAngle - angle;
            ctx.moveTo((headRadius * Math.sin(startPointAngle)), (headRadius * Math.cos(startPointAngle)));
            ctx.quadraticCurveTo(tailCurveRadius * Math.sin(startPointAngle + Math.PI / 24), tailCurveRadius * Math.cos(startPointAngle + Math.PI / 24), tailEndRadius * Math.sin(startPointAngle), tailEndRadius * Math.cos(startPointAngle));
            ctx.quadraticCurveTo(tailCurveRadius * Math.sin(startPointAngle + Math.PI / 18), tailCurveRadius * Math.cos(startPointAngle + Math.PI / 18), (headRadius * Math.sin(startPointAngle + Math.PI / 2)), (headRadius * Math.cos(startPointAngle + Math.PI / 2)));
            ctx.fill();
            ctx.restore();
        }
    }, []);

    const drawKusarigamaPattern = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, aS: number, r: () => number) => {
        ctx.lineWidth = iR * seededRandom(r, 0.03, 0.05);
        const s = pR * 1.5, m = iR * seededRandom(r, 0.6, 0.8), e = iR * seededRandom(r, 0.85, 0.95);
        const cX = m * 0.8, cY = m * seededRandom(r, 0.8, 1.2) * Math.sin(aS / 2);
        ctx.beginPath(); ctx.moveTo(s, 0); ctx.quadraticCurveTo(cX, cY, e, 0); ctx.quadraticCurveTo(cX, -cY, s, 0); ctx.closePath(); ctx.fill();
    }, [seededRandom]);
    const drawShurikenStarPattern = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, aS: number, r: () => number) => {
        ctx.lineWidth = iR * seededRandom(r, 0.02, 0.04);
        const iR2 = pR * seededRandom(r, 1.8, 2.5), oR = iR * seededRandom(r, 0.8, 0.95);
        const iA = aS / 2 * seededRandom(r, 0.8, 1.2);
        ctx.beginPath(); ctx.moveTo(oR, 0); ctx.lineTo(iR2 * Math.cos(iA), iR2 * Math.sin(iA)); ctx.lineTo(oR * Math.cos(aS), oR * Math.sin(aS)); ctx.quadraticCurveTo(0, 0, oR, 0); ctx.closePath(); ctx.fill();
    }, [seededRandom]);
    const drawLotusPetalPattern = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, aS: number, r: () => number) => {
        ctx.lineWidth = iR * seededRandom(r, 0.02, 0.03);
        const bR = pR * 1.5, tR = iR * seededRandom(r, 0.85, 0.95), pW = tR * Math.sin(aS / 2) * seededRandom(r, 0.7, 0.9);
        ctx.beginPath(); ctx.moveTo(bR, 0); ctx.bezierCurveTo(tR * 0.8, pW, tR * 0.8, pW, tR, 0); ctx.bezierCurveTo(tR * 0.8, -pW, tR * 0.8, -pW, bR, 0); ctx.closePath(); ctx.fill();
    }, [seededRandom]);
    const drawVoidScythePattern = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, aS: number, r: () => number) => {
        ctx.lineWidth = iR * seededRandom(r, 0.04, 0.06);
        const s = pR * 1.2, mR = iR * seededRandom(r, 0.6, 0.75), eR = iR * seededRandom(r, 0.8, 0.95);
        const mA = aS * seededRandom(r, 0.2, 0.3);
        ctx.beginPath(); ctx.moveTo(s, 0); ctx.quadraticCurveTo(mR * Math.cos(mA), mR * Math.sin(mA), eR, 0); ctx.stroke();
    }, [seededRandom]);
    const drawBladedSunPattern = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, aS: number, r: () => number) => {
        const iR2 = pR * 1.2, oR = iR * seededRandom(r, 0.9, 1), bW = aS / 2 * seededRandom(r, 0.7, 0.9);
        ctx.beginPath(); ctx.moveTo(iR2, 0); ctx.arc(0, 0, oR, -bW, bW); ctx.closePath(); ctx.fill();
    }, [seededRandom]);
    const drawShisuiPinwheelPattern = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, r: () => number) => {
        const s = pR * 1.1, e = iR * seededRandom(r, 0.9, 0.95), m = (s + e) / 2;
        ctx.beginPath(); ctx.moveTo(s, 0); ctx.quadraticCurveTo(m, m * seededRandom(r, 0.4, 0.65), e, 0); ctx.lineWidth = iR * seededRandom(r, 0.14, 0.18); ctx.stroke();
    }, [seededRandom]);
    const drawConcentricRing = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, r: () => number) => {
        ctx.lineWidth = iR * seededRandom(r, 0.015, 0.03);
        const rR = iR * seededRandom(r, 0.6, 0.75);
        ctx.beginPath(); ctx.arc(0, 0, rR, 0, 2 * Math.PI); ctx.stroke();
    }, [seededRandom]);
    const drawBarbedRing = useCallback((ctx: CanvasRenderingContext2D, pR: number, iR: number, nS: number, r: () => number) => {
        ctx.lineWidth = iR * seededRandom(r, 0.01, 0.02);
        const rR = iR * seededRandom(r, 0.65, 0.75), bL = rR * 0.1;
        ctx.beginPath(); ctx.arc(0, 0, rR, 0, 2 * Math.PI); ctx.stroke();
        for(let i = 0; i < nS * 2; i++) {
            const a = (i * Math.PI) / nS;
            const sX = rR * Math.cos(a), sY = rR * Math.sin(a), eX = (rR - bL) * Math.cos(a), eY = (rR - bL) * Math.sin(a);
            ctx.beginPath(); ctx.moveTo(sX, sY); ctx.lineTo(eX, eY); ctx.stroke();
        }
    }, [seededRandom]);

    const drawPrimaryPattern = useCallback((ctx: CanvasRenderingContext2D, type: number, pR: number, iR: number, aS: number, r: () => number) => {
        switch (type) {
            case 0: drawKusarigamaPattern(ctx, pR, iR, aS, r); break;
            case 1: drawShurikenStarPattern(ctx, pR, iR, aS, r); break;
            case 2: drawLotusPetalPattern(ctx, pR, iR, aS, r); break;
            case 3: drawVoidScythePattern(ctx, pR, iR, aS, r); break;
            case 4: drawBladedSunPattern(ctx, pR, iR, aS, r); break;
            case 5: drawShisuiPinwheelPattern(ctx, pR, iR, r); break;
        }
    }, [drawKusarigamaPattern, drawShurikenStarPattern, drawLotusPetalPattern, drawVoidScythePattern, drawBladedSunPattern, drawShisuiPinwheelPattern]);

    const drawSecondaryPattern = useCallback((ctx: CanvasRenderingContext2D, type: number, pR: number, iR: number, nS: number, r: () => number) => {
         switch (type) {
            case 0: drawConcentricRing(ctx, pR, iR, r); break;
            case 1: drawBarbedRing(ctx, pR, iR, nS, r); break;
        }
    }, [drawConcentricRing, drawBarbedRing]);

    const drawMangekyoPattern = useCallback((ctx: CanvasRenderingContext2D, seed: number, radius: number, center: Point, primaryPattern: number, rotation = 0, final = false) => {
        const rand = createSeededRandom(seed);
        const pupilRadius = radius * seededRandom(rand, 0.12, 0.20);
        const isInverted = primaryPattern === 6;

        if (final) {
            drawBaseIris(ctx, radius, center, isInverted);
            drawPupil(ctx, pupilRadius, center);
        }

        ctx.save();
        ctx.translate(center.x, center.y);
        ctx.rotate(rotation);

        if (isInverted) {
            ctx.strokeStyle = '#c50000'; ctx.fillStyle = '#ff1a1a'; ctx.shadowColor = 'rgba(255, 0, 0, 0.5)';
        } else {
            ctx.strokeStyle = 'black'; ctx.fillStyle = 'black'; ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
        }

        ctx.lineCap = 'round'; ctx.shadowBlur = 8; ctx.shadowOffsetX = 2; ctx.shadowOffsetY = 2;

        const secondaryPattern = rand() > 0.4 ? seededRandomInt(rand, 0, 1) : -1;
        let patternToDraw = isInverted ? seededRandomInt(rand, 0, 4) : primaryPattern;
        if (patternToDraw === 5 && isInverted) patternToDraw = 0;

        let numSymmetry = patternToDraw === 5 ? 4 : [3, 4, 5][seededRandomInt(rand, 0, 2)];
        const angleStep = (2 * Math.PI) / numSymmetry;

        for (let i = 0; i < numSymmetry; i++) {
            const angle = i * angleStep;
            ctx.save();
            ctx.rotate(angle);
            if (secondaryPattern !== -1 && !isInverted) drawSecondaryPattern(ctx, secondaryPattern, pupilRadius, radius, numSymmetry, rand);
            drawPrimaryPattern(ctx, patternToDraw, pupilRadius, radius, angleStep, rand);
            ctx.restore();
        }
        ctx.restore();
    }, [drawBaseIris, drawPupil, seededRandom, seededRandomInt, drawPrimaryPattern, drawSecondaryPattern]);

    const drawInitialSharingan = useCallback((ctx: CanvasRenderingContext2D) => {
        const width = ctx.canvas.width; const height = ctx.canvas.height;
        const center = { x: width / 2, y: height / 2 };
        const radius = width / 2 - 10;
        const pupilRadius = radius * 0.22;
        ctx.clearRect(0, 0, width, height);
        drawBaseIris(ctx, radius, center);
        drawPupil(ctx, pupilRadius, center);
        drawTomoe(ctx, radius);
    }, [drawBaseIris, drawPupil, drawTomoe]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;

        const resizeCanvas = () => {
            const container = canvas.parentElement;
            if (container) {
                const size = container.clientWidth;
                canvas.width = size;
                canvas.height = size;
                if (lastDrawnState.current?.type === 'ms') {
                    const { seed, primaryPattern } = lastDrawnState.current;
                    const radius = canvas.width / 2 - 10;
                    const center = { x: canvas.width / 2, y: canvas.height / 2 };
                    drawMangekyoPattern(context, seed, radius, center, primaryPattern, 0, true);
                } else {
                    drawInitialSharingan(context);
                }
            }
        };

        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        return () => window.removeEventListener('resize', resizeCanvas);
    }, [drawInitialSharingan, drawMangekyoPattern]);

    useEffect(() => {
        if (!animationTrigger || isAnimating.current) return;

        isAnimating.current = true;
        const { seed, userDescription, primaryPattern } = animationTrigger;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;

        const duration = 2500;
        let startTime: number | null = null;

        const animationStep = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const elapsedTime = timestamp - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            const width = context.canvas.width; const height = context.canvas.height;
            const center = { x: width / 2, y: height / 2 };
            const radius = width / 2 - 10;
            const isInverted = primaryPattern === 6;

            context.clearRect(0, 0, width, height);
            drawBaseIris(context, radius, center, isInverted);

            const totalSpins = 5;
            const easeOutProgress = 1 - Math.pow(1 - progress, 4);
            const mangekyoRotation = easeOutProgress * Math.PI * 2 * totalSpins;

            context.save();
            context.globalAlpha = Math.sin(progress * Math.PI / 2);
            drawMangekyoPattern(context, seed, radius, center, primaryPattern, mangekyoRotation);
            context.restore();

            const tomoeRotation = progress * Math.PI * 8;
            context.save();
            context.globalAlpha = 1 - Math.pow(progress, 2);
            drawTomoe(context, radius, tomoeRotation);
            context.restore();

            const rand = createSeededRandom(seed);
            const startPupilRadius = radius * 0.22;
            const endPupilRadius = radius * seededRandom(rand, 0.12, 0.20);
            const currentPupilRadius = startPupilRadius + (endPupilRadius - startPupilRadius) * progress;
            drawPupil(context, currentPupilRadius, center);

            if (progress < 1) {
                requestAnimationFrame(animationStep);
            } else {
                isAnimating.current = false;
                lastDrawnState.current = { type: 'ms', seed, primaryPattern };
                drawMangekyoPattern(context, seed, radius, center, primaryPattern, 0, true);
                onAnimationComplete(userDescription, primaryPattern);
            }
        };

        requestAnimationFrame(animationStep);

    }, [animationTrigger, onAnimationComplete, drawBaseIris, drawMangekyoPattern, drawPupil, drawTomoe, seededRandom]);

    return <canvas ref={canvasRef} className="rounded-full w-full h-auto" style={{ aspectRatio: '1 / 1' }} />;
};
